/**
 * Enhanced Cache System
 * 
 * Multi-layer caching with TTL, size limits, and persistence options.
 * Replaces the basic ChunkCache with a comprehensive solution.
 */

import { z } from 'zod';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl?: number;
  accessCount: number;
  lastAccessed: number;
  version?: number; // Data version for conflict detection
  serverTimestamp?: number; // Timestamp from server for ordering
  etag?: string; // ETag for HTTP cache validation
}

export interface CacheOptions {
  maxSize?: number;
  defaultTtl?: number;
  enablePersistence?: boolean;
  storageKey?: string;
  enableVersioning?: boolean; // Enable version tracking
  checkStale?: boolean; // Check for stale data
  staleThresholdMs?: number | null; // Override stale threshold (null disables)
  validator?: z.ZodSchema; // Schema for data validation
  strictValidation?: boolean; // Throw on validation errors
}

export interface CacheStats {
  size: number;
  maxSize: number;
  hitRate: number;
  totalHits: number;
  totalMisses: number;
  memoryUsage: number;
  staleHits?: number; // Hits that were stale and rejected
  versionConflicts?: number; // Version conflicts detected
  validationErrors?: number; // Validation failures
  lastCleanup?: number; // Timestamp of last cleanup
}

export type CacheLayer = 'memory' | 'session' | 'persistent';

export class EnhancedCache<T = any> {
  private cache: Map<string, CacheEntry<T>> = new Map();
  private maxSize: number;
  private defaultTtl: number;
  private enablePersistence: boolean;
  private storageKey: string;
  private layer: CacheLayer;
  
  private hits = 0;
  private misses = 0;
  private staleHits = 0;
  private versionConflicts = 0;
  private validationErrors = 0;
  private cleanupInterval?: NodeJS.Timeout;
  private currentVersion = 0; // Monotonic version counter
  private validator?: z.ZodSchema;
  private strictValidation = false;
  private checkStale = true;
  private staleThresholdMs = 300000;
  private saveTimeout?: NodeJS.Timeout;
  private saveDebounceMs = 200;

  constructor(layer: CacheLayer, options: CacheOptions = {}) {
    this.layer = layer;
    this.maxSize = options.maxSize || (layer === 'memory' ? 100 : 50);
    this.defaultTtl = options.defaultTtl || (layer === 'memory' ? 300000 : 3600000); // 5min memory, 1hr session/persistent
    this.enablePersistence = options.enablePersistence || false;
    this.storageKey = options.storageKey || `grandline_cache_${layer}`;
    
    // Versioning options (currently always enabled)
    // const enableVersioning = options.enableVersioning !== false; // Default true
    // const checkStale = options.checkStale !== false; // Default true
    
    // Validation options
    this.validator = options.validator;
    this.strictValidation = options.strictValidation || false;

    // Stale checking options
    this.checkStale = options.checkStale !== false;
    if (options.staleThresholdMs === null) {
      this.checkStale = false;
      this.staleThresholdMs = 0;
    } else if (options.staleThresholdMs !== undefined) {
      this.staleThresholdMs = options.staleThresholdMs;
    }
    
    // Load from storage if persistent
    if (this.enablePersistence && (layer === 'session' || layer === 'persistent')) {
      this.loadFromStorage();
    }
    
    // Start cleanup interval
    this.startCleanup();
  }

  /**
   * Get data from cache
   */
  public get(key: string): T | undefined {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.misses++;
      return undefined;
    }
    
    // Check TTL
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      this.misses++;
      return undefined;
    }
    
    // Check for stale data if enabled
    if (this.isStale(entry)) {
      this.cache.delete(key);
      this.staleHits++;
      this.misses++;
      console.warn(`Cache entry ${key} is stale, removing`);
      return undefined;
    }
    
    // Update access stats
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    this.hits++;
    
    return entry.data;
  }

  /**
   * Set data in cache with optional version info
   */
  public set(key: string, data: T, options?: {
    ttl?: number;
    version?: number;
    serverTimestamp?: number;
    etag?: string;
    force?: boolean; // Force overwrite even if version conflicts
    skipValidation?: boolean; // Skip validation for this set
  }): boolean {
    // Validate data if validator is present
    if (this.validator && !options?.skipValidation) {
      try {
        this.validator.parse(data);
      } catch (error) {
        this.validationErrors++;
        if (this.strictValidation) {
          throw error;
        }
        console.error(`Cache validation failed for ${key}:`, error);
        return false;
      }
    }
    const existingEntry = this.cache.get(key);
    if (existingEntry && options?.version !== undefined && 
        existingEntry.version !== undefined && 
        !options?.force && 
        options.version <= existingEntry.version) {
      console.warn(`Version conflict for ${key}: existing ${existingEntry.version} >= new ${options.version}`);
      this.versionConflicts++;
      return false;
    }
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: options?.ttl || this.defaultTtl,
      accessCount: 0,
      lastAccessed: Date.now(),
      version: options?.version ?? ++this.currentVersion,
      serverTimestamp: options?.serverTimestamp,
      etag: options?.etag,
    };
    
    // Enforce size limit
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLeastRecentlyUsed();
    }
    
    this.cache.set(key, entry);
    
    // Persist if enabled
    if (this.enablePersistence && (this.layer === 'session' || this.layer === 'persistent')) {
      this.saveToStorage();
    }
    
    return true;
  }

  /**
   * Check if key exists and is not expired
   */
  public has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    if (this.isExpired(entry)) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Delete specific key
   */
  public delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    
    if (deleted && this.enablePersistence && (this.layer === 'session' || this.layer === 'persistent')) {
      this.saveToStorage();
    }
    
    return deleted;
  }

  /**
   * Clear all cache
   */
  public clear(): void {
    this.cache.clear();
    
    if (this.enablePersistence && (this.layer === 'session' || this.layer === 'persistent')) {
      this.clearStorage();
    }
  }

  /**
   * Get cache statistics
   */
  public getStats(): CacheStats {
    const total = this.hits + this.misses;
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: total > 0 ? this.hits / total : 0,
      totalHits: this.hits,
      totalMisses: this.misses,
      memoryUsage: this.estimateMemoryUsage(),
      staleHits: this.staleHits,
      versionConflicts: this.versionConflicts,
      validationErrors: this.validationErrors,
      lastCleanup: Date.now(),
    };
  }

  /**
   * Get all keys (for debugging)
   */
  public keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Force cleanup of expired entries
   */
  public cleanup(): number {
    let cleaned = 0;
    
    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        cleaned++;
      }
    }
    
    return cleaned;
  }

  /**
   * Destroy cache and cleanup resources
   */
  public destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.flush();
    
    this.cache.clear();
  }

  public flush(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = undefined;
    }

    if (this.enablePersistence) {
      this.flushToStorage();
    }
  }

  // Private methods

  private isExpired(entry: CacheEntry<T>): boolean {
    if (!entry.ttl) return false;
    return Date.now() - entry.timestamp > entry.ttl;
  }
  
  private isStale(entry: CacheEntry<T>): boolean {
    if (!this.checkStale || this.staleThresholdMs <= 0) return false;
    if (!entry.serverTimestamp) return false;
    return Date.now() - entry.serverTimestamp > this.staleThresholdMs;
  }
  
  /**
   * Check if cached data is newer than provided timestamp
   */
  public isNewerThan(key: string, timestamp: number): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    const entryTime = entry.serverTimestamp || entry.timestamp;
    return entryTime > timestamp;
  }
  
  /**
   * Get version info for a key
   */
  public getVersionInfo(key: string): { version?: number; etag?: string; timestamp: number } | undefined {
    const entry = this.cache.get(key);
    if (!entry) return undefined;
    
    return {
      version: entry.version,
      etag: entry.etag,
      timestamp: entry.serverTimestamp || entry.timestamp,
    };
  }

  private evictLeastRecentlyUsed(): void {
    let oldestKey = '';
    let oldestTime = Date.now();
    
    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  private startCleanup(): void {
    // Cleanup expired entries every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 300000);
  }

  private estimateMemoryUsage(): number {
    // Rough estimation in bytes
    let size = 0;
    for (const [key, entry] of Array.from(this.cache.entries())) {
      size += key.length * 2; // UTF-16 characters
      size += JSON.stringify(entry.data).length * 2;
      size += 64; // Entry metadata overhead
    }
    return size;
  }

  // Storage methods for session/persistent layers

  private loadFromStorage(): void {
    try {
      const storage = this.layer === 'session' ? sessionStorage : localStorage;
      const data = storage.getItem(this.storageKey);
      
      if (data) {
        const parsed = JSON.parse(data);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const now = Date.now();
        
        for (const [key, entry] of Object.entries(parsed)) {
          const typedEntry = entry as CacheEntry<T>;
          
          // Skip expired entries
          if (typedEntry.ttl && now - typedEntry.timestamp > typedEntry.ttl) {
            continue;
          }
          
          this.cache.set(key, typedEntry);
        }
      }
    } catch (error) {
      console.warn(`Failed to load cache from ${this.layer} storage:`, error);
    }
  }

  private saveToStorage(): void {
    if (this.saveTimeout) {
      return;
    }

    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = undefined;
      this.flushToStorage();
    }, this.saveDebounceMs);
  }

  private flushToStorage(): void {
    try {
      const storage = this.layer === 'session' ? sessionStorage : localStorage;
      const data = Object.fromEntries(this.cache.entries());
      storage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.warn(`Failed to save cache to ${this.layer} storage:`, error);
    }
  }

  private clearStorage(): void {
    try {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
        this.saveTimeout = undefined;
      }
      const storage = this.layer === 'session' ? sessionStorage : localStorage;
      storage.removeItem(this.storageKey);
    } catch (error) {
      console.warn(`Failed to clear cache from ${this.layer} storage:`, error);
    }
  }
}
